import { FormGroup } from '@angular/forms';
import {
  ReviewLocationEnum,
  ReviewLocationSchema,
  WorkspaceCreateV2,
  ClassicStudyWorkspaceCreate,
  SiteWorkspaceCreate,
  MTRWorkspaceCreate,
  AppealsWorkspaceCreate,
} from '@data-access/submissions';
import {
  SELECT_MULTI_SITE_SUBMISSION_TYPES,
  SELECT_REGION_SUBMISSION_TYPES,
  IRB_PLUS_SUBMISSION_TYPES,
} from '@data-access/submissions/constants/setup.constants';
import { SubmissionSetupForm } from './types';
import { SetupRoutes, WorkspaceTypes } from './constants';
import { GridColumns } from '@wcg/frontend-framework';

/**
 * Priority is only required for submission types that support IRB+ processing.
 * @param submissionType
 * @returns true if priority is required, false otherwise
 */
function isPriorityRequired(submissionType: string): boolean {
  return IRB_PLUS_SUBMISSION_TYPES.includes(submissionType);
}

/**
 * Determine if the "Next" button should be disabled based on the current route and form values.
 * @param workspaceType
 * @param route
 * @param formValues
 * @returns true if "Next" should be disabled, false otherwise
 * @throws Error if the current route is unexpected for the given workspace type
 */
export function isNextDisabled(
  workspaceType: string,
  route: string,
  formGroup?: FormGroup,
): boolean {
  const formValues = formGroup?.getRawValue() as SubmissionSetupForm; // Use getRawValue() to include disabled controls
  // Always disable if there is no title or no workspace type selected
  const all_sites: boolean = formValues.allSitesOrStudy === 'allSites';

  // For appeals setup, only submission title is required
  if (route === SetupRoutes.appealsSetup) {
    return !formValues?.submissionTitle;
  }

  if (!formValues?.submissionTitle || !formValues?.submissionType) {
    return true;
  }
  // Validate required fields for New Study submissions
  else if (
    workspaceType === WorkspaceTypes.newStudy &&
    route === SetupRoutes.studySetup
  ) {
    if (!formValues.usInternationalReview && !formValues.canadianReview) {
      return true;
    }
    const priorityRequired = isPriorityRequired(formValues.submissionType);
    return (
      !formValues.protocolTitle ||
      !formValues.sponsor ||
      !formValues.sponsorProtocolNumber ||
      !formValues.studyPlusSite ||
      (priorityRequired && !formValues.priority)
    );
  }
  // Validate required fields for CIR submissions
  else if (
    workspaceType === WorkspaceTypes.cir &&
    route === SetupRoutes.selectStudy
  ) {
    return !formValues?.study;
  }
  // Validate required fields for MTR submissions
  else if (workspaceType === WorkspaceTypes.mtr) {
    if (route === SetupRoutes.selectStudy) {
      return !formValues?.study;
    } else if (route === SetupRoutes.mtrSetup) {
      if (!formValues?.usInternationalReview && !formValues?.canadianReview) {
        return true;
      } else if (
        formValues?.allSitesOrStudy === 'allSites' ||
        formValues?.allSitesOrStudy === 'selectSites'
      ) {
        return !all_sites
          ? !formValues?.siteIds || formValues?.siteIds.length === 0
          : false;
      }
      return formValues?.allSitesOrStudy !== 'onlyStudy';
    } else if (route === SetupRoutes.selectSite) {
      return !formValues?.entityId;
    }
  }

  // Validate required fields for Copy Submission submissions
  if (route === SetupRoutes.copySubmission) {
    return formGroup?.get('copySubmissionStep')?.valid === false;
  }

  return false;
}

/**
 * Get the next navigation step based on the current workspace type, subtype, and route.
 * @param workspaceType
 * @param workspaceSubType
 * @param route
 * @returns The next step route or undefined if at the end
 * @throws Error if the current route is unexpected for the given workspace type
 */
export function getNextStep(
  workspaceType: string,
  workspaceSubType: string,
  route: string,
): string | undefined | void {
  // Handle appeals submission - appeals go directly to create submission
  if (route === SetupRoutes.appealsSetup) {
    return SetupRoutes.createSubmission;
  }

  if (workspaceType === WorkspaceTypes.newStudy) {
    if (route === SetupRoutes.selectSubType) {
      return SetupRoutes.studySetup;
    } else if (route === SetupRoutes.studySetup) {
      return SetupRoutes.copySubmission;
    } else if (route === SetupRoutes.copySubmission) {
      return SetupRoutes.createSubmission;
    }
    throw new Error('Unexpected route for New Study submission type.');
  } else if (workspaceType === WorkspaceTypes.cir) {
    if (route === SetupRoutes.selectSubType) {
      return SetupRoutes.selectStudy;
    } else if (route === SetupRoutes.selectStudy) {
      return SetupRoutes.copySubmission;
    } else if (route === SetupRoutes.copySubmission) {
      return SetupRoutes.createSubmission;
    }
    throw new Error(
      'Unexpected route for Add PI/Site to Existing Study submission type.',
    );
  } else if (workspaceType === WorkspaceTypes.mtr) {
    if (route === SetupRoutes.selectSubType) {
      return SetupRoutes.selectStudy;
    } else if (route === SetupRoutes.selectStudy) {
      if (SELECT_REGION_SUBMISSION_TYPES.includes(workspaceSubType)) {
        return SetupRoutes.mtrSetup;
      }
      return SetupRoutes.selectSite;
    } else if (route === SetupRoutes.mtrSetup) {
      return SetupRoutes.copySubmission;
    } else if (route === SetupRoutes.selectSite) {
      return SetupRoutes.copySubmission;
    } else if (route === SetupRoutes.copySubmission) {
      return SetupRoutes.createSubmission;
    }
    throw new Error(
      'Unexpected route for Modification to Research submission type.',
    );
  }
}

/**
 * Validate and create a WorkspaceCreateV2 object from the form values for Initial Review submissions.
 * @param form
 * @param reviewLocations
 * @returns WorkspaceCreateV2 object
 * @throws Error if required fields are missing or invalid
 */
export function irSubmissionFromForm(
  form: FormGroup,
  reviewLocations: ReviewLocationSchema[],
): WorkspaceCreateV2 {
  const submissionType = form.get('submissionType')?.value;
  if (!submissionType) {
    throw new Error('Submission type is required to create a workspace.');
  }

  const name = form.get('submissionTitle')?.value;
  if (!name) {
    throw new Error('Submission name is required to create a workspace.');
  }

  const sponsor = form.get('sponsor')?.value;
  if (!sponsor) {
    throw new Error('Sponsor is required to create a workspace.');
  }

  const sponsorProtocolNumber = form.get('sponsorProtocolNumber')?.value || '';
  if (!sponsorProtocolNumber) {
    throw new Error(
      'Sponsor protocol number is required to create a workspace.',
    );
  }

  const reviewLocationId = getReviewLocationId(form, reviewLocations);

  const priority = form.get('priority')?.value || '';

  const studyPlusSite = form.get('studyPlusSite')?.value === 'yes';

  const studyTitle = form.get('protocolTitle')?.value;
  if (!studyTitle) {
    throw new Error('Study title is required to create a workspace.');
  }

  const reviewBoard = form.get('reviewBoard')?.value || 'irb';

  const copySubmissionStep = form.get('copySubmissionStep') as FormGroup;
  const sourceSubmissionTypeId =
    _getCopyFromSourceSubmissionId(copySubmissionStep);

  return {
    entity_id: undefined,
    entity_type: 'STUDY',
    data: {},
    name,
    sites: {},
    site_ids: [],
    study_id: undefined,
    review_location_id: reviewLocationId,
    priority,
    sponsor_name: sponsor,
    sponsor_protocol_id: sponsorProtocolNumber,
    title: studyTitle,
    modules: {},
    review_board: reviewBoard,
    type: submissionType,
    is_new_study_and_site: studyPlusSite,
    source_submission_id: sourceSubmissionTypeId,
  };
}

/**
 * Create a StudyWorkspaceCreate payload from form values for Initial Review submissions.
 * Uses the new POST /v2/workspace/new-study/ endpoint.
 * @param form
 * @param reviewLocations
 * @returns StudyWorkspaceCreate object
 * @throws Error if required fields are missing or invalid
 */
export function studyWorkspaceFromForm(
  form: FormGroup,
  reviewLocations: ReviewLocationSchema[],
): ClassicStudyWorkspaceCreate {
  const submissionType = form.get('submissionType')?.value;
  if (!submissionType) {
    throw new Error('Submission type is required to create a workspace.');
  }

  const name = form.get('submissionTitle')?.value;
  if (!name) {
    throw new Error('Submission name is required to create a workspace.');
  }

  const sponsor = form.get('sponsor')?.value;
  if (!sponsor) {
    throw new Error('Sponsor is required to create a workspace.');
  }

  const sponsorProtocolNumber = form.get('sponsorProtocolNumber')?.value || '';
  if (!sponsorProtocolNumber) {
    throw new Error(
      'Sponsor protocol number is required to create a workspace.',
    );
  }

  const reviewLocationId = getReviewLocationId(form, reviewLocations);

  const priority = form.get('priority')?.value || '';

  const studyPlusSite = form.get('studyPlusSite')?.value === 'yes';

  const studyTitle = form.get('protocolTitle')?.value;
  if (!studyTitle) {
    throw new Error('Study title is required to create a workspace.');
  }

  const reviewBoard = form.get('reviewBoard')?.value || 'irb';

  const copySubmissionStep = form.get('copySubmissionStep') as FormGroup;
  const sourceSubmissionTypeId =
    _getCopyFromSourceSubmissionId(copySubmissionStep);

  return {
    type: submissionType,
    name,
    review_location_id: reviewLocationId,
    priority,
    is_new_study_and_site: studyPlusSite,
    title: studyTitle,
    sponsor_protocol_id: sponsorProtocolNumber,
    sponsor_name: sponsor,
    review_board: reviewBoard,
    source_submission_id: sourceSubmissionTypeId,
  };
}

/** * Validate and create a WorkspaceCreateV2 object from the form values for CIR submissions.
 * @param form
 * @returns WorkspaceCreateV2 object for CIR submissions
 * @throws Error if required fields are missing or invalid
 */
export function cirSubmissionFromForm(form: FormGroup): WorkspaceCreateV2 {
  const submissionType = form.get('submissionType')?.value;
  if (!submissionType) {
    throw new Error('Submission type is required to create a workspace.');
  }

  const title = form.get('submissionTitle')?.value;
  if (!title) {
    throw new Error('Submission title is required to create a workspace.');
  }

  const study = form.get('study')?.value;

  if (!study || !study.id) {
    throw new Error(
      'A valid study selection is required to create a workspace.',
    );
  }

  const copySubmissionStep = form.get('copySubmissionStep') as FormGroup;
  const sourceSubmissionTypeId =
    _getCopyFromSourceSubmissionId(copySubmissionStep);

  const value: WorkspaceCreateV2 = {
    entity_id: undefined,
    entity_type: 'SITE',
    data: {},
    name: title,
    sites: {},
    site_ids: [],
    study_id: study.id,
    review_location_id: undefined,
    priority: '',
    sponsor_name: study.sponsor_name || '',
    sponsor_protocol_id: study.sponsor_protocol_id || '',
    title,
    modules: {},
    review_board: 'irb',
    type: submissionType,
    is_new_study_and_site: false,
    source_submission_id: sourceSubmissionTypeId,
  };

  return value;
}

/**
 * Create a SiteWorkspaceCreate payload from form values for CIR submissions.
 * Uses the new POST /v2/workspace/new-site/ endpoint.
 * @param form
 * @returns SiteWorkspaceCreate object for CIR submissions
 * @throws Error if required fields are missing or invalid
 */
export function siteWorkspaceFromForm(form: FormGroup): SiteWorkspaceCreate {
  const submissionType = form.get('submissionType')?.value;
  if (!submissionType) {
    throw new Error('Submission type is required to create a workspace.');
  }

  const name = form.get('submissionTitle')?.value;
  if (!name) {
    throw new Error('Submission title is required to create a workspace.');
  }

  const study = form.get('study')?.value;

  if (!study || !study.id) {
    throw new Error(
      'A valid study selection is required to create a workspace.',
    );
  }

  const copySubmissionStep = form.get('copySubmissionStep') as FormGroup;
  const sourceSubmissionTypeId =
    _getCopyFromSourceSubmissionId(copySubmissionStep);

  return {
    type: submissionType,
    name,
    study_id: study.id,
    source_submission_id: sourceSubmissionTypeId,
  };
}

/**
 * Validate and create a WorkspaceCreateV2 object from the form values for Modification to Research submissions.
 * @param form
 * @param reviewLocations
 * @returns WorkspaceCreateV2 object for MTR submissions
 * @throws Error if required fields are missing or invalid
 */
export function mtrSubmissionFromForm(
  form: FormGroup,
  reviewLocations: ReviewLocationSchema[],
): WorkspaceCreateV2 {
  const submissionType = form.get('submissionType')?.value;
  if (!submissionType) {
    throw new Error('Submission type is required to create a workspace.');
  }

  const title = form.get('submissionTitle')?.value;
  if (!title) {
    throw new Error('Submission title is required to create a workspace.');
  }

  const study = form.get('study')?.value;

  if (!study || !study.id) {
    throw new Error(
      'A valid study selection is required to create a workspace.',
    );
  }

  let entityId;
  let entityType;
  let siteIds: string[] = [];
  let exclude_site_ids: string[] = [];
  const all_sites: boolean = form.get('allSitesOrStudy')?.value === 'allSites';

  if (!all_sites) {
    siteIds = form.get('siteIds')?.value || [];
  } else {
    exclude_site_ids = form.get('siteIds')?.value || [];
  }
  // Determine if selecting all sites, only study, or some sites
  if (!SELECT_MULTI_SITE_SUBMISSION_TYPES.includes(submissionType)) {
    entityType = 'SITE';
    entityId = form.get('entityId')?.value;
  } else if (form.get('allSitesOrStudy')?.value === 'onlyStudy') {
    entityId = study.id;
    entityType = 'STUDY';
  } else if (
    all_sites ||
    form.get('allSitesOrStudy')?.value === 'selectSites'
  ) {
    entityId = form.get('entityId')?.value;
    entityType = 'SITE';
    if (!all_sites && siteIds.length === 0) {
      throw new Error(
        'At least one site must be selected to create a workspace.',
      );
    }
  } else {
    throw new Error('Invalid site selection option.');
  }

  let reviewLocationId;
  // Do not pass review location ID if submission type does not require region selection
  if (SELECT_REGION_SUBMISSION_TYPES.includes(submissionType)) {
    reviewLocationId = getReviewLocationId(form, reviewLocations);
  }

  const copySubmissionStep = form.get('copySubmissionStep') as FormGroup;
  const sourceSubmissionTypeId =
    _getCopyFromSourceSubmissionId(copySubmissionStep);

  const value: WorkspaceCreateV2 = {
    entity_id: entityId,
    entity_type: entityType,
    data: {},
    name: title,
    sites: {},
    site_ids: siteIds,
    study_id: study.id,
    review_location_id: reviewLocationId,
    priority: '',
    sponsor_name: study.sponsor_name || '',
    sponsor_protocol_id: study.sponsor_protocol_id || '',
    title,
    modules: {},
    review_board: 'irb',
    type: submissionType,
    is_new_study_and_site: false,
    source_submission_id: sourceSubmissionTypeId,
    all_sites,
    exclude_site_ids,
  };

  return value;
}

/**
 * Create an MTRWorkspaceCreate payload from form values for Modification to Research submissions.
 * Uses the new POST /v2/workspace/mtr/ endpoint.
 * @param form
 * @param reviewLocations
 * @returns MTRWorkspaceCreate object for MTR submissions
 * @throws Error if required fields are missing or invalid
 */
export function mtrWorkspaceFromForm(
  form: FormGroup,
  reviewLocations: ReviewLocationSchema[],
): MTRWorkspaceCreate {
  const submissionType = form.get('submissionType')?.value;
  if (!submissionType) {
    throw new Error('Submission type is required to create a workspace.');
  }

  const name = form.get('submissionTitle')?.value;
  if (!name) {
    throw new Error('Submission title is required to create a workspace.');
  }

  const study = form.get('study')?.value;

  if (!study || !study.id) {
    throw new Error(
      'A valid study selection is required to create a workspace.',
    );
  }

  let entityId: string | undefined;
  let entityType: string;
  let siteIds: string[] = [];
  let exclude_site_ids: string[] = [];
  const all_sites: boolean = form.get('allSitesOrStudy')?.value === 'allSites';

  if (!all_sites) {
    siteIds = form.get('siteIds')?.value || [];
  } else {
    exclude_site_ids = form.get('siteIds')?.value || [];
  }
  // Determine if selecting all sites, only study, or some sites
  if (!SELECT_MULTI_SITE_SUBMISSION_TYPES.includes(submissionType)) {
    entityType = 'site';
    entityId = form.get('entityId')?.value;
  } else if (form.get('allSitesOrStudy')?.value === 'onlyStudy') {
    entityId = study.id;
    entityType = 'study';
  } else if (
    all_sites ||
    form.get('allSitesOrStudy')?.value === 'selectSites'
  ) {
    entityId = form.get('entityId')?.value;
    entityType = 'site';
    if (!all_sites && siteIds.length === 0) {
      throw new Error(
        'At least one site must be selected to create a workspace.',
      );
    }
  } else {
    throw new Error('Invalid site selection option.');
  }

  let reviewLocationId: string | undefined;
  // Do not pass review location ID if submission type does not require region selection
  if (SELECT_REGION_SUBMISSION_TYPES.includes(submissionType)) {
    reviewLocationId = getReviewLocationId(form, reviewLocations);
  }

  const copySubmissionStep = form.get('copySubmissionStep') as FormGroup;
  const sourceSubmissionTypeId =
    _getCopyFromSourceSubmissionId(copySubmissionStep);

  return {
    type: submissionType,
    name,
    entity_id: entityId,
    entity_type: entityType,
    study_id: study.id,
    site_ids: siteIds,
    review_location_id: reviewLocationId,
    source_submission_id: sourceSubmissionTypeId,
    all_sites,
    exclude_site_ids,
  };
}

/**
 * Create an AppealsWorkspaceCreate payload from form values for Appeals submissions.
 * Uses the new POST /v2/workspace/{workspace_2}/appeal endpoint.
 * Only the study name (submission title) is required for appeals.
 * @param form
 * @returns AppealsWorkspaceCreate object for Appeals submissions
 * @throws Error if required fields are missing or invalid
 */
export function appealsWorkspaceFromForm(
  form: FormGroup,
): AppealsWorkspaceCreate {
  const submissionType = form.get('submissionType')?.value || '';
  const name = form.get('submissionTitle')?.value;

  if (!name) {
    throw new Error(
      'Submission name (study name) is required to create an appeals workspace.',
    );
  }

  // Get site selection from form
  const allSites = form.get('allSitesOrStudy')?.value === 'allSites';

  const siteIds = form.get('siteIds')?.value || [];
  const exclude_site_ids = allSites ? siteIds : [];

  // Get optional fields
  const copySubmissionStep = form.get('copySubmissionStep') as FormGroup;
  const sourceSubmissionTypeId =
    _getCopyFromSourceSubmissionId(copySubmissionStep);

  return {
    type: submissionType,
    name,
    site_ids: allSites ? [] : siteIds,
    all_sites: allSites,
    exclude_site_ids,
    source_submission_id: sourceSubmissionTypeId,
  };
}

/**
 * Determine the review location ID based on form selections and available review locations.
 * @param form
 * @param reviewLocations
 * @returns The ID of the selected review location.
 * @throws Error if no valid review location is selected or available.
 */
export function getReviewLocationId(
  form: FormGroup,
  reviewLocations: ReviewLocationSchema[],
): string {
  const isUsAndInternationalReview = form.get('usInternationalReview')?.value;
  const isCanadianReview = form.get('canadianReview')?.value;
  let reviewLocationId;

  if (!isUsAndInternationalReview && !isCanadianReview) {
    throw new Error(
      'At least one review location (US/International or Canadian) must be selected to create a workspace.',
    );
  } else if (!reviewLocations.length) {
    throw new Error(
      'No review locations are available. Please contact support.',
    );
  } else if (isUsAndInternationalReview && isCanadianReview) {
    reviewLocationId = reviewLocations.find(
      (loc) =>
        loc.name.toLowerCase() ===
        ReviewLocationEnum.USInternationalAndCanadian,
    )?.id;
  } else if (isUsAndInternationalReview) {
    reviewLocationId = reviewLocations.find(
      (loc) => loc.name.toLowerCase() === ReviewLocationEnum.USInternational,
    )?.id;
  } else if (isCanadianReview) {
    reviewLocationId = reviewLocations.find(
      (loc) => loc.name.toLowerCase() === ReviewLocationEnum.Canadian,
    )?.id;
  }

  if (!reviewLocationId) {
    throw new Error(
      'Selected review location is invalid. Please contact support.',
    );
  }
  return reviewLocationId;
}

/**
 * Get the source submission ID from the copy submission step form.
 * @param form
 * @returns The source submission ID or undefined if no submission is selected
 */
const _getCopyFromSourceSubmissionId = (
  form?: FormGroup,
): string | undefined => {
  if (!form) {
    console.warn('No form provided to get source submission ID');
    return undefined;
  }
  const selectedSubmission = form.get('selectedSubmission')?.value;
  if (selectedSubmission) {
    return String(selectedSubmission.id);
  }
  return undefined;
};

/**
 * Utility function to update columns with a specified sortable property.
 *
 * @param columns - The original columns object.
 * @param sortable - The value to set for the sortable property.
 * @returns A new columns object with the sortable property updated.
 */
export function updateColumnsSortable(
  columns: GridColumns,
  sortable: boolean,
): GridColumns {
  return Object.keys(columns).reduce((acc, key) => {
    acc[key] = {
      ...columns[key],
      sortable,
    };
    return acc;
  }, {} as GridColumns);
}
